import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Droplets, Wallet, Clock, ExternalLink,
  CheckCircle2, AlertCircle, Loader2, Copy, Shield, Fuel
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/lib/backendUrl';


function formatTime(seconds) {
  if (seconds <= 0) return 'Now';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function FaucetPage() {
  const [wallet, setWallet] = useState('');
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dripping, setDripping] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/api/faucet/info`)
      .then(r => r.json())
      .then(setInfo)
      .catch(() => {});
  }, []);

  const checkStatus = async (addr) => {
    if (!addr || addr.length !== 42) { setStatus(null); return; }
    try {
      const res = await fetch(`${API}/api/faucet/status/${addr}`);
      const data = await res.json();
      setStatus(data);
    } catch { setStatus(null); }
  };

  const handleWalletChange = (val) => {
    setWallet(val);
    setError('');
    setResult(null);
    if (val.startsWith('0x') && val.length === 42) checkStatus(val);
    else setStatus(null);
  };

  const handleDrip = async () => {
    if (!wallet.startsWith('0x') || wallet.length !== 42) {
      setError('Enter a valid 0x wallet address (42 characters)');
      return;
    }
    setDripping(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(`${API}/api/faucet/drip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Drip failed');
      setResult(data);
      toast.success(`${data.amount} HERMES sent!`);
      checkStatus(wallet);
      // Refresh info
      fetch(`${API}/api/faucet/info`).then(r => r.json()).then(setInfo).catch(() => {});
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setDripping(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-gray-100" data-testid="faucet-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-white/5 bg-[#0a0e1a]/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/protocol" className="text-gray-400 hover:text-white transition-colors" data-testid="back-to-protocol">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center">
                <Droplets className="w-4 h-4 text-white" />
              </div>
              <span className="heading-font text-base font-semibold tracking-tight">Testnet Faucet</span>
            </div>
          </div>
          <Badge variant="outline" className="border-[#F7931A]/30 text-[#F7931A] bg-[#F7931A]/5 text-xs">
            Base Sepolia
          </Badge>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F7931A]/20 to-[#B08D57]/10 border border-[#F7931A]/20 mb-5">
            <Droplets className="w-8 h-8 text-[#F7931A]" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl heading-font font-bold tracking-tight mb-3" data-testid="faucet-heading">
            $HERMES Testnet Faucet
          </h1>
          <p className="text-gray-400 max-w-md mx-auto text-sm md:text-base">
            Get free test tokens on Base Sepolia to experiment with the BHERMES Protocol.
          </p>
        </div>

        {/* Stats bar */}
        {info && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="faucet-stats">
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-[#F7931A] text-lg font-bold mono heading-font">{info.drip_amount}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">HERMES / Drip</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-gray-100 text-lg font-bold mono heading-font">{info.cooldown_hours}h</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Cooldown</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-gray-100 text-lg font-bold mono heading-font">{info.faucet_balance?.toLocaleString()}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Faucet Balance</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-gray-100 text-lg font-bold mono heading-font">{info.total_drips}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Total Drips</p>
            </div>
          </div>
        )}

        {/* Main card */}
        <Card className="bg-white/[0.03] border-white/5 overflow-hidden" data-testid="faucet-card">
          <CardContent className="p-6 sm:p-8">
            {!result ? (
              <div className="space-y-5">
                <div>
                  <label className="text-xs text-gray-400 mb-2 block font-medium">Wallet Address</label>
                  <div className="relative">
                    <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      value={wallet}
                      onChange={e => handleWalletChange(e.target.value)}
                      placeholder="0x..."
                      className="pl-10 h-12 bg-white/[0.03] border-white/10 text-gray-100 placeholder:text-gray-600 mono text-sm focus:border-[#F7931A]/40 focus:ring-[#F7931A]/20"
                      data-testid="faucet-wallet-input"
                    />
                  </div>
                </div>

                {/* Cooldown status */}
                {status && !status.can_drip && (
                  <div className="flex items-center gap-3 bg-amber-500/5 border border-amber-500/15 rounded-xl px-4 py-3" data-testid="cooldown-notice">
                    <Clock className="w-5 h-5 text-amber-500 shrink-0" />
                    <div>
                      <p className="text-sm text-amber-400 font-medium">Cooldown Active</p>
                      <p className="text-xs text-gray-500">
                        Next drip available in {formatTime(status.remaining_seconds)}
                      </p>
                    </div>
                  </div>
                )}

                {status && status.can_drip && wallet.length === 42 && (
                  <div className="flex items-center gap-3 bg-emerald-500/5 border border-emerald-500/15 rounded-xl px-4 py-3" data-testid="ready-notice">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                    <p className="text-sm text-emerald-400">Ready to drip {info?.drip_amount || 10} HERMES</p>
                  </div>
                )}

                {error && (
                  <div className="flex items-center gap-3 bg-red-500/5 border border-red-500/15 rounded-xl px-4 py-3" data-testid="error-notice">
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                <Button
                  onClick={handleDrip}
                  disabled={dripping || !wallet || wallet.length !== 42 || (status && !status.can_drip)}
                  className="w-full h-12 bg-gradient-to-r from-[#F7931A] to-[#B08D57] hover:from-[#F7931A]/90 hover:to-[#B08D57]/90 text-black font-semibold text-base disabled:opacity-40"
                  data-testid="drip-button"
                >
                  {dripping ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending on-chain...
                    </>
                  ) : (
                    <>
                      <Droplets className="w-5 h-5 mr-2" />
                      Request {info?.drip_amount || 10} HERMES
                    </>
                  )}
                </Button>
              </div>
            ) : (
              /* Success result */
              <div className="space-y-5" data-testid="drip-result">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
                    <CheckCircle2 className="w-7 h-7 text-emerald-500" />
                  </div>
                  <h2 className="text-xl font-bold heading-font text-gray-100">Tokens Sent!</h2>
                  <p className="text-sm text-gray-400 mt-1">
                    <span className="text-[#F7931A] font-bold mono">{result.amount} HERMES</span> sent to your wallet
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-white/[0.03] rounded-lg px-4 py-3">
                    <span className="text-xs text-gray-500">Transaction</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs mono text-gray-300">{result.tx_hash?.slice(0, 10)}...{result.tx_hash?.slice(-8)}</span>
                      <button onClick={() => copyToClipboard(result.tx_hash)} className="text-gray-500 hover:text-gray-300">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <a href={result.explorer_url} target="_blank" rel="noopener noreferrer" className="text-[#F7931A] hover:text-[#F7931A]/80">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center justify-between bg-white/[0.03] rounded-lg px-4 py-3">
                    <span className="text-xs text-gray-500">Block</span>
                    <span className="text-xs mono text-gray-300">#{result.block_number}</span>
                  </div>
                  <div className="flex items-center justify-between bg-white/[0.03] rounded-lg px-4 py-3">
                    <span className="text-xs text-gray-500">Faucet Remaining</span>
                    <span className="text-xs mono text-gray-300">{result.faucet_remaining?.toLocaleString()} HERMES</span>
                  </div>
                </div>

                <Button
                  onClick={() => { setResult(null); setWallet(''); setStatus(null); }}
                  variant="outline"
                  className="w-full h-11 border-white/10 text-gray-300 hover:bg-white/5"
                  data-testid="drip-again-btn"
                >
                  Request Again
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info section */}
        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4">
            <Fuel className="w-5 h-5 text-[#F7931A] mb-2" />
            <h3 className="text-sm font-semibold heading-font text-gray-200 mb-1">Gas-Free Tokens</h3>
            <p className="text-xs text-gray-500 leading-relaxed">
              You still need Base Sepolia ETH for gas. Get it from the{' '}
              <a href="https://www.alchemy.com/faucets/base-sepolia" target="_blank" rel="noopener noreferrer" className="text-[#F7931A] hover:underline">
                Base Sepolia Faucet
              </a>.
            </p>
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4">
            <Shield className="w-5 h-5 text-[#F7931A] mb-2" />
            <h3 className="text-sm font-semibold heading-font text-gray-200 mb-1">Rate Limited</h3>
            <p className="text-xs text-gray-500 leading-relaxed">
              {info?.drip_amount || 10} HERMES per wallet every {info?.cooldown_hours || 24} hours. Keeps the faucet available for everyone.
            </p>
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4">
            <Droplets className="w-5 h-5 text-[#F7931A] mb-2" />
            <h3 className="text-sm font-semibold heading-font text-gray-200 mb-1">What to Test</h3>
            <p className="text-xs text-gray-500 leading-relaxed">
              Use tokens for marketplace purchases, staking, DAO governance voting, and booking on the protocol.
            </p>
          </div>
        </div>

        {/* Token contract */}
        {info?.token_address && (
          <div className="mt-6 text-center">
            <p className="text-[10px] text-gray-600 mb-1">HERMES Token Contract</p>
            <button
              onClick={() => copyToClipboard(info.token_address)}
              className="inline-flex items-center gap-1.5 text-xs mono text-gray-500 hover:text-gray-300 transition-colors"
              data-testid="token-contract-address"
            >
              {info.token_address}
              <Copy className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
